import { useMemo, useRef, useState } from 'react';
import { Heading, State, ConversationItem } from '@/components/atoms';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/molecules';
import { Link } from 'react-router';
import { ArrowRightIcon } from 'lucide-react';
import { routes } from '@/lib/constants';
import {
  mapClassificationToLeadStatus,
  type LeadStatus,
} from '@/lib/omnichannel/classification';
import { useOmnichannelConversationLists } from '@/hooks/use-omnichannel-conversation-lists';
import { useOmnichannelSocket } from '@/pages/dashboard/conversations/hooks/useOmnichannelSocket';
import type { ChannelType, Conversation } from '@/types';

function withLeadStatus(
  conversations: Conversation[],
  leadStatusByConversationId: Record<string, LeadStatus>
): Conversation[] {
  return conversations.map((c) => ({
    ...c,
    status: leadStatusByConversationId[c.id] ?? c.status,
  }));
}

export const Conversations = () => {
  const [activeTab, setActiveTab] = useState<ChannelType | 'all'>('all');
  const [leadStatusByConversationId, setLeadStatusByConversationId] = useState<
    Record<string, LeadStatus>
  >({});

  const selectedConversationRef = useRef('');
  const {
    conversationsByChannel,
    allConversations,
    hasAnyAccountConnected,
    isLoading,
  } = useOmnichannelConversationLists();

  useOmnichannelSocket(hasAnyAccountConnected, {
    selectedConversationIdRef: selectedConversationRef,
    onAiReplyDraft: () => {},
    onAiClassification: (classification, conversationId) => {
      const status = mapClassificationToLeadStatus(classification);
      if (!status) return;
      setLeadStatusByConversationId((prev) =>
        prev[conversationId] === status
          ? prev
          : { ...prev, [conversationId]: status }
      );
    },
  });

  const { list, counts } = useMemo(() => {
    const facebook = withLeadStatus(
      conversationsByChannel.facebook,
      leadStatusByConversationId
    );
    const instagram = withLeadStatus(
      conversationsByChannel.instagram,
      leadStatusByConversationId
    );
    const whatsapp = withLeadStatus(
      conversationsByChannel.whatsapp,
      leadStatusByConversationId
    );
    const all = withLeadStatus(allConversations, leadStatusByConversationId);

    return {
      list:
        activeTab === 'all'
          ? all
          : activeTab === 'whatsapp'
            ? whatsapp
            : activeTab === 'instagram'
              ? instagram
              : facebook,
      counts: {
        all: all.reduce((sum, chat) => sum + (chat.unread ?? 0), 0),
        whatsapp: whatsapp.reduce((sum, chat) => sum + (chat.unread ?? 0), 0),
        instagram: instagram.reduce((sum, chat) => sum + (chat.unread ?? 0), 0),
        facebook: facebook.reduce((sum, chat) => sum + (chat.unread ?? 0), 0),
      },
    };
  }, [
    activeTab,
    allConversations,
    conversationsByChannel,
    leadStatusByConversationId,
  ]);

  const renderList = () => (
    <div className="flex flex-col max-h-[280px] overflow-auto">
      {list.map((item) => (
        <Link
          key={`${item.channel}-${item.id}`}
          to={`${routes.conversations}?channel=${item.channel}&conversationId=${item.id}`}
          className="block"
        >
          <ConversationItem {...item} />
        </Link>
      ))}
    </div>
  );

  return (
    <div className="col-span-5 lg:col-span-3 flex flex-col gap-4 p-4 md:p-6 bg-white rounded-md border border-accent">
      <Heading>Recent Chat</Heading>
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ChannelType | 'all')}
      >
        <div className="overflow-x-auto py-2">
          <TabsList>
            <TabsTrigger count={counts.all} value="all">
              All
            </TabsTrigger>
            <TabsTrigger count={counts.whatsapp} value="whatsapp">
              WhatsApp
            </TabsTrigger>
            <TabsTrigger count={counts.instagram} value="instagram">
              Instagram
            </TabsTrigger>
            <TabsTrigger count={counts.facebook} value="facebook">
              Facebook
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="all">
          {isLoading ? (
            <State description="Loading…" />
          ) : list.length === 0 ? (
            <State description="You don't have any recent chat yet" />
          ) : (
            renderList()
          )}
        </TabsContent>
        <TabsContent value="whatsapp">
          {isLoading ? (
            <State description="Loading…" />
          ) : list.length === 0 ? (
            <State description="You don't have any WhatsApp conversation yet" />
          ) : (
            renderList()
          )}
        </TabsContent>
        <TabsContent value="instagram">
          {isLoading ? (
            <State description="Loading…" />
          ) : list.length === 0 ? (
            <State description="You don't have any Instagram conversation yet" />
          ) : (
            renderList()
          )}
        </TabsContent>
        <TabsContent value="facebook">
          {isLoading ? (
            <State description="Loading…" />
          ) : list.length === 0 ? (
            <State description="You don't have any Facebook conversation yet" />
          ) : (
            renderList()
          )}
        </TabsContent>
      </Tabs>
      <div className="mt-auto">
        <Link
          className="text-sm text-primary flex items-center gap-1"
          to={routes.conversations}
        >
          All customers
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};
